import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { FC } from "react";

/**
 * Represents a dashboard widget descriptor.
 * This is a purely client-side UI concept — the widget objects are constructed
 * in dashboardWidgetMapper.tsx and are never fetched directly from the backend.
 */
export interface DashboardWidget {
  /** React component rendered as the widget tile */
  component: FC;
  // Every optional member carries its `| undefined` arm: the widget descriptors are assembled field
  // by field in dashboardWidgetMapper.tsx, where an absent member and one built as `undefined` are
  // the same thing to every reader below, and `Exact<>`-wrapped payloads (setProjectModalVisiblity)
  // map each member through `T[K] | undefined`.
  title?: string | undefined;
  /** Navigation path for the widget share/navigate action */
  path?: string | undefined;
  propertiesOption?: Array<{ id: string; text: string }> | undefined;
  /** Whether the widget supports expand/maximize mode */
  expand?: boolean | undefined;
  isAdded?: boolean | undefined;
  allowIn?: string[] | undefined;
  layout?: { w: number; h: number; x: number; y: number } | undefined;
  previewImg?: string | undefined;
  module?: string | undefined;
  /** Arbitrary additional widget data (e.g. pre-loaded row data, config) */
  data?: Record<string, string | number | boolean | null | undefined> | undefined;
}

export interface ProjectDashboardState {
  isModalVisible: boolean;
  title: string;
  /** The React component to render inside the modal */
  component: FC | null;
  patchForProject: boolean;
  /** Additional widget data passed to the modal */
  widget?: DashboardWidget | undefined;
  widgetName?: string | undefined;
}

/**
 * The patch payload `setProjectModalVisiblity` accepts. NOT `Partial<ProjectDashboardState>`: that makes
 * every key merely OPTIONAL, and under `exactOptionalPropertyTypes` an optional key may be ABSENT but never
 * present-holding-`undefined`. type-fest's `ExactObject` is a homomorphic mapping that re-emits each
 * optional member as `k?: V | undefined`, so a payload constrained by `Exact<Partial<State>, T>` is not
 * assignable back to `Partial<State>`. Naming the `| undefined` arm here fixes that, and it is what the
 * call sites really hold; the `Exact<>` guard is UNCHANGED.
 */
type ProjectDashboardPatch = { [K in keyof ProjectDashboardState]?: ProjectDashboardState[K] | undefined };

const initialProjectDashboardDataState: ProjectDashboardState = {
  isModalVisible: false,
  title: "",
  component: null,
  patchForProject: false,
};

const projectDashboardSlice = createSlice({
  name: "projectDashboard",
  initialState: initialProjectDashboardDataState,
  reducers: {
    setProjectModalVisiblity: (state: ProjectDashboardState, action: PayloadAction<ProjectDashboardPatch>) => {
      Object.assign(state, action.payload);
    },
    setPatchForProject: (state: ProjectDashboardState, action: PayloadAction<boolean>) => {
      state.patchForProject = action.payload;
    },
  },
});

const { setProjectModalVisiblity: _setProjectModalVisiblityRaw } = projectDashboardSlice.actions;
export const { setPatchForProject } = projectDashboardSlice.actions;

/**
 * Object-payload action creator wrapped with `Exact<>` so excess properties are caught even when the
 * payload is passed through a variable.
 *
 * ⚠️ KEEP THE CONSTRAINT FORM. Moving this to the parameter form (`<T extends P>(payload: T & Exact<P, T>)`)
 * to get past `exactOptionalPropertyTypes` does NOT keep excess keys mapping to `never`: with `T` inferred
 * from the argument, the intersection is checked against the already-inferred `T` and excess keys pass.
 * That was caught only because two `@ts-expect-error` directives at dashboardWidgetCard.tsx went UNUSED.
 * If eOPT reports this creator again, name the payload type (see the patch type above) or fix the CALL SITE;
 * do not loosen the guard.
 */
export const setProjectModalVisiblity = <T extends Exact<ProjectDashboardPatch, T>>(payload: T) =>
  _setProjectModalVisiblityRaw(payload);

export const projectDashboardData = projectDashboardSlice.reducer;

export default projectDashboardSlice.reducer;
